<template>
  <div class="bg-light card">
    <div class="px-3 pt-2 border-bottom">
      <label for="location" class="font-weight-bold">Location</label>
      <span class="custom-asterisk-added">&nbsp;&#42;</span>
      <span v-if="locationId">
        <i class="fas fa-map-marker-alt text-success"></i>
        <span>{{ name }}</span>
      </span>
      <span v-else-if="!locationId && name && lngLat.lat && lngLat.lng">
        <i class="fas fa-map-marker-alt text-primary"></i>
        <span>{{ name }} is a new location</span>
      </span>
      <span v-else-if="!locationId && !name && lngLat.lat && lngLat.lng">
        <i class="fas fa-map-marker-alt text-danger"></i>
        <span>Location needs a name</span>
      </span>
      <span v-else>
        <i class="fas fa-map-marker-alt text-danger"></i> None selected
      </span>
    </div>
    <div class="row no-gutters">
      <div class="panel-list col-md-6 col-lg-4 d-flex flex-column">
        <div class="flex-none">
          <b-input-group size="md">
            <b-input-group-prepend>
              <span class="btn btn-secondary rounded-0" title="Search">
                <i v-if="true" class="fas fa-search"></i>
                <i v-if="false" class="fas fa-spinner fa-spin"></i>
              </span>
            </b-input-group-prepend>
            <b-form-input
              class="form-control rounded-0"
              placeholder="search by location name"
              name="search"
              type="search"
              :value="searchTerm"
              @input="setSearchTerm($event)"
              debounce="1000"
              autocomplete="off"
            ></b-form-input>
            <b-input-group-append v-if="searchTerm">
              <b-button @click="setSearchTerm('')" class="rounded-0"
                ><i class="fas fa-times-circle"></i
              ></b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
        <div class="locations-list flex-grow-1 overflow-auto">
          <div
            v-if="userAssignments.length > 0"
            class="p-0 bg-white text-center border-bottom"
          >
            <i class="fa fa-user align-self-center mr-2"></i> Assigned to you
          </div>
          <div
            v-for="assignment in userAssignments"
            :key="assignment['id']"
            :ref="'locationItem-' + assignment['@id']"
            class="border-bottom d-flex flex-row flex-none assignment"
            :class="{ active: assignment.location['@id'] === locationId }"
            @click="itemClick(assignment)"
          >
            <div class="flex-none d-flex pl-2">
              <i class="fa fa-user align-self-center"></i>
            </div>
            <div class="flex-grow-1 p-2">
              <div>{{ assignment.location.name }}</div>
              <div class="small">
                {{
                  `${assignment.location.lngLat.lat}, ${assignment.location.lngLat.lng}`
                }}
              </div>
            </div>
            <div
              class="assignment-chevron flex-none pl-1 pr-1 d-flex align-items-center"
            >
              <i class="fa fa-chevron-right align-self-center"></i>
            </div>
          </div>
          <div
            v-if="datasheetAssignments.length > 0"
            class="p-0 bg-white text-center border-bottom"
          >
            <i class="fa fa-rectangle-list align-self-center mr-2"></i>
            Assigned to this datasheet
          </div>
          <div
            v-for="assignment in datasheetAssignments"
            :key="assignment['id']"
            :ref="'locationItem-' + assignment['@id']"
            class="border-bottom d-flex flex-row flex-none assignment"
            :class="{ active: assignment.location['@id'] === locationId }"
            @click="itemClick(assignment)"
          >
            <div class="flex-none d-flex pl-2">
              <i class="fa fa-rectangle-list align-self-center"></i>
            </div>
            <div class="flex-grow-1 p-2">
              <div>{{ assignment.location.name }}</div>
              <div class="small">
                {{
                  `${assignment.location.lngLat.lat}, ${assignment.location.lngLat.lng}`
                }}
              </div>
            </div>
            <div
              class="assignment-chevron flex-none pl-1 pr-1 d-flex align-items-center"
            >
              <i class="fa fa-chevron-right align-self-center"></i>
            </div>
          </div>
          <div
            v-if="projectAssignments.length > 0"
            class="p-0 bg-white text-center border-bottom"
          >
            <i class="fa fa-users align-self-center mr-2"></i>
            Assigned to everyone in this project
          </div>
          <div
            v-for="assignment in projectAssignments"
            :key="assignment['id']"
            :ref="'locationItem-' + assignment['@id']"
            class="border-bottom d-flex flex-row flex-none assignment"
            :class="{ active: assignment.location['@id'] === locationId }"
            @click="itemClick(assignment)"
          >
            <div class="flex-none d-flex pl-2">
              <i class="fa fa-users align-self-center"></i>
            </div>
            <div class="flex-grow-1 p-2">
              <div>{{ assignment.location.name }}</div>
              <div class="small">
                {{
                  `${assignment.location.lngLat.lat}, ${assignment.location.lngLat.lng}`
                }}
              </div>
            </div>
            <div
              class="assignment-chevron flex-none pl-1 pr-1 d-flex align-items-center"
            >
              <i class="fa fa-chevron-right align-self-center"></i>
            </div>
          </div>

          <div v-if="isLoading" class="text-center p-3 text-muted">
            <i class="fa fa-spinner fa-spin"></i> Loading Locations...
          </div>
          <div v-if="!isLoading && allAssignments.length === 0" class="text-center p-3 text-muted">
            <div class="font-weight-bold">No Locations Have been assigned to you.</div>
            <div>You must create a new location for this observation</div>
            <div class="btn btn-primary btn-block mt-2" @click="clickNew">
              <i class="fa fa-plus mr-1"></i> New
            </div>
          </div>
          <div
            v-if="GEOCODE_SUGGESTIONS.length > 0"
            class="p-0 bg-white text-muted text-center border-bottom"
          >
            <i class="fa fa-globe align-self-center mr-2"></i>
            Other places
          </div>
          <div
            v-for="suggestion in GEOCODE_SUGGESTIONS"
            :key="suggestion.magicKey"
            class="border-bottom d-flex flex-row flex-none suggestion"
            @click="handleSelectGeocode(suggestion)"
          >
            <div class="flex-grow-1 p-2">
              <div>{{ suggestion.text }}</div>
            </div>
            <div class="flex-none d-flex pr-2">
              <i class="fa fa-globe align-self-center"></i>
            </div>
            <div
              class="assignment-chevron flex-none pl-1 pr-1 d-flex align-items-center"
            >
              <i class="fa fa-chevron-right align-self-center"></i>
            </div>
          </div>
        </div>
        <div v-if="message" :class="`p-2 flex-none ${message.type}`">
          {{ message.text }}
        </div>
        <div class="flex-none d-none d-md-block">
          <div class="btn btn-primary btn-block rounded-0" @click="clickNew">
            <i class="fa fa-plus mr-1"></i> New
          </div>
        </div>
      </div>
      <div class="panel-map col-md-6 col-lg-8 position-relative border">
        <LMap
          class="location-map"
          :zoom="map.zoom"
          :center="map.center"
          @update:center="centerUpdate"
          @click="mapClick"
          ref="leaflet"
          @ready="addBasemapLayer(BASEMAP_ACTIVE)"
          :options="map.options"
        >
          <LLayerGroup>
            <LMarker
              :lat-lng="this.lngLat"
              v-if="!locationId && this.lngLat.lat && this.lngLat.lng"
              :draggable="true"
              @moveend="dragendNew"
            >
              <MarkerPin :class="{ primary: true }"
                ><i class="fa fa-plus"></i
              ></MarkerPin>
              <LTooltip>
                <div>New location</div>
                <div v-if="this.name">{{ this.name }}</div>
                <div v-else class="font-weight-bold text-danger">
                  Needs a name
                </div>
                <div>Drag to move</div>
              </LTooltip>
            </LMarker>
            <LMarker
              v-for="assignment in closestAssignments"
              :key="assignment['id']"
              :lat-lng.sync="assignment.location.lngLat"
              @click="markerClick(assignment)"
              :draggable="false"
              v-if="
                assignment.location.lngLat.lat && assignment.location.lngLat.lng
              "
            >
              <MarkerPin
                :type="
                  assignment.location['@id'] === locationId ? 'active' : null
                "
              ></MarkerPin>
              <LTooltip
                ><div>{{ assignment.name }}</div>
                <div>Click to select</div></LTooltip
              >
            </LMarker>
            <LMarker :lat-lng="home" v-if="home && home.lat && home.lng">
              <MarkerPin><i class="fa fa-home"></i></MarkerPin>
              <LTooltip content="Project Home" />
            </LMarker>
            <LRectangle
              v-if="searchArea.area && searchArea.area.bounds"
              :bounds="searchArea.area.bounds"
              :l-style="searchArea.style"
            >
            </LRectangle>

            <LCircle
              v-else-if="
                searchArea.area &&
                searchArea.area.lngLat &&
                searchArea.area.accuracy
              "
              :lat-lng="searchArea.area.lngLat"
              :radius="searchArea.area.accuracy"
              :l-style="searchArea.style"
            >
            </LCircle>
          </LLayerGroup>
          <LControlZoom position="bottomright"></LControlZoom>
        </LMap>
        <div class="map-layers bg-white border rounded position-absolute">
          <BaseMapPicker class="p-1" />
        </div>
        <div
          class="position-absolute map-help text-center"
          v-if="!isMapClicked"
          :class="{
            border: errors.has('latitude') || errors.has('longitude'),
            'border-danger': errors.has('latitude') || errors.has('longitude'),
          }"
        >
          <div>
            To select a location: Click / tap a marker on the map or in the list
          </div>
          <div>
            If you need to create a new location, click the "New" button.
          </div>
        </div>
        <div
          class="position-absolute map-help text-center"
          v-else-if="isNewLocation && !lngLat.lat && !lngLat.lng"
          :class="{
            border: errors.has('latitude') || errors.has('longitude'),
            'border-danger': errors.has('latitude') || errors.has('longitude'),
          }"
        >
          <div>Click / tap on map</div>
          <div>to set the coordinates for this new location</div>
        </div>
        <GpsInput
          v-on:input="setGps"
          ref="GpsInput"
          class="position-absolute map-search"
        />
      </div>
    </div>
    <div class="flex-none d-block d-md-none">
      <div class="btn btn-primary btn-block rounded-0" @click="clickNew">
        <i class="fa fa-plus mr-1"></i> New
      </div>
    </div>
    <div class="location-new mx-3 pt-1 border-top" v-if="isNewLocation">
      <div class="row">
        <b-form-group
          id="name-input-group"
          class="col-12 col-md-6"
          label="New Location Name"
          label-for="name"
        >
          <b-form-input
            id="name"
            ref="name"
            v-bind:value="name"
            v-on:input="updateInputNewName"
            name="name"
            placeholder="Please name this location..."
            v-validate="{
              required: true,
              min: 5,
              max: 100,
            }"
            data-vv-delay="500"
            :class="{
              'is-invalid': errors.has(`name`),
            }"
            aria-describedby="name-live-feedback"
          ></b-form-input>
          <b-form-invalid-feedback
            id="name-live-feedback"
            v-if="errors.has('name')"
          >
            {{ errors.first("name") }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
          id="latitude-input-group"
          class="col-6 col-md-3"
          label="Latitude"
          label-for="latitude"
        >
          <b-form-input
            id="latitude"
            v-bind:value="lngLat.lat"
            v-on:input="updateInputNewLat"
            type="number"
            :number="true"
            name="latitude"
            placeholder="±90.0000000"
            :step="0.0005"
            v-validate="{
              required: true,
              decimal: 7,
              min_value: -90,
              max_value: 90,
            }"
            data-vv-delay="2000"
            :class="{
              'is-invalid': errors.has(`latitude`),
            }"
            aria-describedby="latitude-live-feedback"
          ></b-form-input>
          <b-form-invalid-feedback
            id="latitude-live-feedback"
            v-if="errors.has('latitude')"
          >
            {{ errors.first("latitude") }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
          id="longitude-input-group"
          class="col-6 col-md-3"
          label="Longitude"
          label-for="longitude"
        >
          <b-form-input
            id="longitude"
            v-bind:value="lngLat.lng"
            v-on:input="updateInputNewLng"
            type="number"
            :number="true"
            name="longitude"
            placeholder="±180.0000000"
            :step="0.0005"
            v-validate="{
              required: true,
              decimal: 7,
              min_value: -180,
              max_value: 180,
            }"
            data-vv-delay="2000"
            :class="{
              'is-invalid': errors.has(`longitude`),
            }"
            aria-describedby="longitude-live-feedback"
          ></b-form-input>
          <b-form-invalid-feedback
            id="longitude-live-feedback"
            v-if="errors.has('longitude')"
          >
            {{ errors.first("longitude") }}
          </b-form-invalid-feedback>
        </b-form-group>
        <div
          class="col-md-12 text-danger mb-3 mt-n2"
          v-if="!name && !lngLat.lat && !lngLat.lng"
        >
          Please enter a location name, and zoom in to select a latitude and
          longitude by either clicking the map or typing it in.
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";

import {
  LMap,
  LTileLayer,
  LLayerGroup,
  LMarker,
  LPopup,
  LTooltip,
  LRectangle,
  LCircle,
  LControlZoom,
} from "vue2-leaflet";

import MarkerCrosshairs from "../../common/map/MarkerCrosshairs.vue";
import MarkerPin from "../../common/map/MarkerPin.vue";
import GpsInput from "../../common/map/gpsInput.vue";
import BaseMapPicker from "../../common/map/BaseMapPicker.vue";

export default {
  name: "DatasheetLocationBoth",
  components: {
    LMap,
    LTileLayer,
    LLayerGroup,
    LMarker,
    LPopup,
    LTooltip,
    LRectangle,
    LCircle,
    LControlZoom,
    MarkerCrosshairs,
    MarkerPin,
    GpsInput,
    BaseMapPicker,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
    home: {
      type: Object,
      default: null,
    },
    datasheetId: {
      type: String,
      default: null,
    },
    projectId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      name: null,
      lngLat: { lng: null, lat: null },
      locationId: null,
      assignmentId: null,

      map: {
        zoom: 12,
        center: { lng: -90, lat: 40 },
        options: {
          zoomControl: false,
        },
        // Used to find the closest assignments before panning finishes, while still allowing panning
        closestCenter: { lng: -90, lat: 40 },
      },

      searchArea: {
        area: null,
        style: {
          color: "#000000",
          weight: 10,
          opacity: 0.8,
          fillOpacity: 0,
        },
        tooltipStyle: {
          permanent: true,
          direction: "top",
          offset: [0, 100],
        },
      },

      isMapClicked: false,
      isNewLocation: false,

      message: null,
    };
  },
  mounted() {
    this.fetchAllDatasheetAssignments({
      datasheetId: this.datasheetId,
      projectId: this.projectId,
    });
    if (this.home && this.home.lat && this.home.lng) {
      this.map.center = this.home;
      this.map.closestCenter = this.home;
    }
  },
  computed: {
    ...mapGetters({
      datasheet: "datasheet/creator/retrievedDatasheet",
      userAssignments: "datasheet/assignedLocations/userAssignments",
      datasheetAssignments: "datasheet/assignedLocations/datasheetAssignments",
      projectAssignments: "datasheet/assignedLocations/projectAssignments",
      allAssignments: "datasheet/assignedLocations/allAssignments",
      fetchedPercent: "datasheet/assignedLocations/fetchedPercent",
      BASEMAP_ACTIVE: "map/BASEMAP_ACTIVE",
    }),
    ...mapState({
      isLoading: (state) => state.datasheet.assignedLocations.isLoading,
      searchTerm: (state) => state.datasheet.assignedLocations.searchTerm,
      GEOCODE_SUGGESTIONS: (state) => state.map.GEOCODE_SUGGESTIONS,
    }),
    closestAssignments() {
      // only show the assignments closest to the center of the map
      // for performance reasons
      if (!this.allAssignments) return [];
      // Calculate distance for each assignment
      const center = this.map.closestCenter;
      return this.allAssignments
        .map((a) => ({
          ...a,
          _distance: Math.sqrt(
            Math.pow(center.lat - a.location.lngLat.lat, 2) +
              Math.pow(center.lng - a.location.lngLat.lng, 2)
          ),
        }))
        .sort((a, b) => a._distance - b._distance)
        .slice(0, 100)
        .reverse();
    },
  },
  methods: {
    // Component methods
    ...mapActions({
      fetchAllDatasheetAssignments:
        "datasheet/assignedLocations/fetchAllDatasheetAssignments",
      ASSIGNED_LOCATIONS_SEARCH: "datasheet/assignedLocations/setSearchTerm",
      GEOCODE_SEARCH: "map/GEOCODE_SEARCH",
      GEOCODE_SELECT: "map/GEOCODE_SELECT",
    }),
    setSearchTerm(term) {
      this.ASSIGNED_LOCATIONS_SEARCH(term);
      this.GEOCODE_SEARCH(term);
    },
    async handleSelectGeocode(suggestion) {
      this.isMapClicked = true;
      try {
        const topCandidate = await this.GEOCODE_SELECT(suggestion);
        if (topCandidate) {
          this.setMapView(topCandidate);
        }
      } catch (error) {
        // vuex message contains the error
      }
    },
    centerUpdate(center) {
      this.map.center = center;
      this.map.closestCenter = center;
    },
    clickNew() {
      console.log("clickNew");
      this.isMapClicked = true;
      this.isNewLocation = true;
      if (this.locationId) {
        this.locationId = null;
        this.assignmentId = null;
        this.name = null;
        this.lngLat = { lng: null, lat: null };
      }
      if (this.searchText) {
        this.name = this.searchText;
        this.searchText = null;
      }
      this.$nextTick(() => {
        let focusRef = this.$refs.name;
        focusRef.$refs.input.focus();
      });
    },
    dragendNew(e) {
      this.lngLat = {
        lng: e.sourceTarget._latlng.lng.toFixed(7),
        lat: e.sourceTarget._latlng.lat.toFixed(7),
      };
      this.updateInputNew();
    },
    setMessage(message) {
      // replace with the store
      this.message = message;
    },
    mapClick(e) {
      this.isMapClicked = true;
      if (this.isNewLocation) {
        this.lngLat = {
          lng: e.latlng.lng.toFixed(7),
          lat: e.latlng.lat.toFixed(7),
        };
        this.updateInputNew();
        if (this.name && this.name.length > 5) {
          return;
        }
        this.$nextTick(() => {
          let focusRef = this.$refs.name;
          focusRef.$refs.input.focus();
        });
      }
    },
    markerClick(assignment) {
      this.setSearchTerm("");
      this.updateInput(assignment);
      this.$nextTick(() => {
        const refName = "locationItem-" + assignment["@id"];
        const el = this.$refs[refName];
        // $refs for v-for is an array, so use [0]
        if (el && el[0]) {
          el[0].scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
      if (this.$refs.leaflet.mapObject.getZoom() < 10) {
        this.$refs.leaflet.mapObject.flyTo(assignment.location.lngLat, 10);
        return;
      }
    },
    itemClick(assignment) {
      this.updateInput(assignment);
      this.$refs.leaflet.mapObject.flyTo(assignment.location.lngLat, 15);
    },
    updateInput(assignment) {
      this.isMapClicked = true;

      this.$validator.reset();
      this.isNewLocation = false;

      this.locationId = assignment.location["@id"];
      this.assignmentId = assignment["@id"];
      this.name = assignment.location.name;
      this.lngLat = { ...assignment.location.lngLat };
      this.map.closestCenter = assignment.location.lngLat;

      this.$emit("input", {
        name: this.name,
        lngLat: this.lngLat,
        "@id": this.locationId,
      });
    },
    updateInputNewName(value) {
      this.name = value;
      this.updateInputNew();
    },
    updateInputNewLat(value) {
      this.lngLat.lat = value;
      this.updateInputNew();
    },
    updateInputNewLng(value) {
      this.lngLat.lng = value;
      this.updateInputNew();
    },
    updateInputNew() {
      console.log("DatasheetLocationBoth updateInputNew");
      this.$emit("input", {
        name: this.name,
        lngLat: this.lngLat,
        "@id": null,
      });
    },
    setGps(searchArea) {
      this.isMapClicked = true;
      this.setSearchTerm("");
      this.setMapView(searchArea);
    },
    setMapView(searchArea) {
      console.log("setMapView", searchArea);
      this.searchArea.area = searchArea;
      if (searchArea && searchArea.bounds) {
        this.$refs.leaflet.mapObject.flyToBounds(searchArea.bounds);
      } else if (searchArea && searchArea.lngLat) {
        this.$refs.leaflet.mapObject.flyTo(searchArea.lngLat, searchArea.zoom);
      }
    },
    panToLocal(lngLat, number = 3) {
      if (!(this.$refs.leaflet && this.$refs.leaflet.mapObject)) {
        // if map is not ready yet, try again shortly
        setTimeout(() => {
          this.panToLocal();
        }, 500);
        return;
      }
      this.map.closestCenter = lngLat;

      const closest = this.allAssignments
        .map((a) => ({
          ...a,
          _distance: Math.sqrt(
            Math.pow(lngLat.lat - a.location.lngLat.lat, 2) +
              Math.pow(lngLat.lng - a.location.lngLat.lng, 2)
          ),
        }))
        .sort((a, b) => a._distance - b._distance)
        .slice(0, number);
      console.log(closest);
      const bounds = closest.map((a) => [
        a.location.lngLat.lat,
        a.location.lngLat.lng,
      ]);
      if (bounds.length > 1) {
        this.$refs.leaflet.mapObject.flyToBounds(bounds, {
          maxZoom: 14,
          padding: [10, 10],
        });
      } else {
        this.$refs.leaflet.mapObject.flyTo(lngLat, 15);
      }
    },
    panToAll() {
      if (!(this.$refs.leaflet && this.$refs.leaflet.mapObject)) {
        // if map is not ready yet, try again shortly
        setTimeout(() => {
          this.panToAll();
        }, 500);
        return;
      }
      this.$nextTick(() => {
        let bounds = this.allAssignments.map((assignment) => [
          assignment.location.lngLat.lat,
          assignment.location.lngLat.lng,
        ]);
        if (this.home && this.home.lat && this.home.lng) {
          bounds.push([this.home.lat, this.home.lng]);
        }
        if (bounds.length >= 2) {
          this.$refs.leaflet.mapObject.flyToBounds(bounds, {
            padding: [10, 10],
          });
        }
      });
    },
    validateLocation() {
      return this.$validator.validateAll().then((result) => {
        console.log(result);
        console.log("validateLocationBoth", this.errors);
        if (this.errors.has("latitude") || this.errors.has("longitude")) {
          if (this.isNewLocation) {
          } else {
            this.isMapClicked = false;
          }
        }
        return result;
      });
    },
    addBasemapLayer(newLayer, oldLayer) {
      const map = this.$refs.leaflet && this.$refs.leaflet.mapObject;
      if (!map || !newLayer) return;
      if (oldLayer) {
        try {
          map.removeLayer(oldLayer);
        } catch (e) {}
      }
      newLayer.addTo(map);
      map.invalidateSize();
    },
  },
  watch: {
    BASEMAP_ACTIVE(newLayer, oldLayer) {
      this.addBasemapLayer(newLayer, oldLayer);
    },
    allAssignments: {
      handler(newVal) {
        this.setMessage(null);
        if (!newVal || newVal.length === 0) {
          return;
        }
        if (newVal.length === 1) {
          // If there is only one assigned location, select it and lock the map on it
          this.updateInput(newVal[0]);
          this.map.closestCenter = newVal[0].location.lngLat;
          this.map.center = newVal[0].location.lngLat;
          this.map.zoom = 15;
          this.map.options.zoomControl = false;
          this.setMessage({
            text: `${newVal[0].location.name} is the only location available to you. It has been selected.`,
            type: "success",
          });
          return;
        }
        if (this.fetchedPercent > 99) {
          this.panToAll();
        }
      },
      immediate: true,
    },
  },
};
</script>
<style scoped>
.panel-map {
}
.panel-list {
  max-height: 30vh;
  min-height: 300px;
}
.location-map {
  width: 100%;
  height: 30vh;
  min-height: 300px;
}
.map-help {
  z-index: 999;
  top: 25%;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 10px;
  border-radius: 10px;
}
.locations-list {
}
.assignment-chevron {
  background-color: rgba(255, 255, 255, 1);
}
.assignment,
.assignment-chevron {
  transition: background-color 0.2s;
  cursor: pointer;
}

.assignment:hover > .assignment-chevron {
  background-color: rgba(135, 152, 63, 0.35);
}
.assignment.active {
  background-color: rgba(135, 152, 63, 0.2);
}
.map-search {
  top: 10px;
  left: 10px;
  right: 10px;
  z-index: 500;

  @media (min-width: 768px) {
    width: 50%;
  }
}
.map-layers {
  z-index: 999;
  bottom: 10px;
  left: 10px;

  @media (min-width: 768px) {
    position: absolute;
    bottom: 10px;
    left: 10px;
    z-index: 999;
  }
}
</style>
